/**
 * HTTP-клиент.
 *
 * Access-токен живёт в памяти вкладки, а не в localStorage: скрипт на странице
 * не должен иметь возможности его прочитать. Refresh лежит в httpOnly-cookie,
 * поэтому продление сессии работает даже после перезагрузки страницы.
 *
 * Типы ответов придут из OpenAPI (`npm run api`) — здесь описаны только те
 * контракты, которыми пользуется фаза 0.
 */

import { randomId } from "@/shared/lib/uuid";
import { API_ERROR_MESSAGES } from "@/shared/api/messages";

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
    request_id?: string;
  };
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown>;

  constructor(status: number, body: ApiErrorBody) {
    super(body.error.message);
    this.name = "ApiError";
    this.status = status;
    this.code = body.error.code;
    this.details = body.error.details ?? {};
  }
}

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;

/**
 * Кого известить, когда продлить сессию не удалось.
 *
 * Через обратный вызов, а не прямым обращением к хранилищу сессии: клиент
 * лежит ниже него и не должен о нём знать — иначе получится круг.
 */
let sessionLost: (() => void) | null = null;

export function onSessionLost(handler: () => void): void {
  sessionLost = handler;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

async function parseError(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as ApiErrorBody;
    return new ApiError(response.status, body);
  } catch {
    // Ответ без тела: до приложения запрос не дошёл (прокси, сеть, таймаут)
    return new ApiError(response.status, {
      error: { code: "network_error", message: API_ERROR_MESSAGES.network },
    });
  }
}

/** Одно продление на все параллельные запросы: иначе ротация съест сама себя. */
async function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const response = await fetch("/api/auth/refresh", { method: "POST" });
      if (!response.ok) {
        return false;
      }
      const body = (await response.json()) as { access_token: string };
      accessToken = body.access_token;
      return true;
    } finally {
      refreshing = null;
    }
  })();

  return refreshing;
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Ключ идемпотентности: мутация, повторённая при обрыве, не выполнится дважды. */
  idempotencyKey?: string;
  /** Внутренний флаг: повтор после продления сессии делается один раз. */
  retried?: boolean;
}

/** Запрос с продлением сессии. Разбор ответа — на вызывающем. */
async function perform(path: string, options: RequestOptions): Promise<Response> {
  const { body, idempotencyKey, retried, headers, ...rest } = options;

  const requestHeaders = new Headers(headers);
  if (body !== undefined) {
    requestHeaders.set("Content-Type", "application/json");
  }
  if (accessToken) {
    requestHeaders.set("Authorization", `Bearer ${accessToken}`);
  }
  if (idempotencyKey) {
    requestHeaders.set("Idempotency-Key", idempotencyKey);
  }

  const response = await fetch(path, {
    ...rest,
    headers: requestHeaders,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  // Вход и продление отвечают 401 по делу: неверный пароль — не потеря сессии
  if (response.status === 401 && !retried && !path.startsWith("/api/auth/")) {
    if (await refreshSession()) {
      return perform(path, { ...options, retried: true });
    }
    // Продлить не вышло: сессия кончилась. Показывать пустые разделы и
    // жаловаться значками — значит делать вид, что данных нет; человека
    // надо вернуть на вход
    sessionLost?.();
  }

  if (!response.ok) {
    throw await parseError(response);
  }
  return response;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await perform(path, options);
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/** Ответ текстом: конфигурация приходит файлом, который человек читает и правит. */
export async function requestText(path: string, options: RequestOptions = {}): Promise<string> {
  return (await perform(path, options)).text();
}

/**
 * Скачать файл, отданный API.
 *
 * Ссылкой в новой вкладке это сделать нельзя: access-токен живёт в памяти
 * вкладки, а не в cookie, и такой запрос уйдёт без него — человек получит
 * страницу с ошибкой вместо файла.
 */
export async function downloadFile(path: string, options: RequestOptions = {}): Promise<void> {
  const response = await perform(path, options);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filenameOf(response.headers.get("Content-Disposition"));
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function filenameOf(disposition: string | null): string {
  const found = /filename="?([^";]+)"?/.exec(disposition ?? "");
  return found?.[1] ?? "export";
}

export function newIdempotencyKey(): string {
  return randomId();
}
